<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-lg-12">
        <div class="d-flex flex-wrap align-items-center justify-content-between mb-4">
          <div>
            <h4 class="mb-3">지식 베이스 관리</h4>
            <p class="mb-0">RAG(Retrieval-Augmented Generation) 기능을 위한 지식 베이스 문서를 관리합니다.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 로딩 표시 -->
    <div v-if="isLoading" class="text-center my-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
      <p class="mt-3">{{ loadingMessage }}</p>
    </div>

    <!-- 에러 메시지 -->
    <b-alert v-if="errorMessage" show variant="danger" dismissible @dismissed="errorMessage = ''">
      {{ errorMessage }}
    </b-alert>

    <!-- 성공 메시지 -->
    <b-alert v-if="successMessage" show variant="success" dismissible @dismissed="successMessage = ''">
      {{ successMessage }}
    </b-alert>

    <div v-if="!isLoading" class="row">
      <!-- 문서 목록 패널 -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header d-flex justify-content-between">
            <div class="header-title">
              <h4 class="card-title">문서 목록</h4>
            </div>
            <div>
              <b-button variant="primary" size="sm" @click="showNewDocumentModal">
                <i class="ri-add-line mr-0"></i> 새 문서
              </b-button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="documents.length === 0" class="text-center p-3">
              <p class="mb-0">등록된 문서가 없습니다.</p>
            </div>
            <ul v-else class="list-group">
              <li
                v-for="doc in documents"
                :key="doc._id"
                class="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                :class="{ active: selectedDocument && selectedDocument.id === doc._id }"
                @click="selectDocument(doc._id)"
              >
                <div>
                  <div>{{ doc.title || doc.filename }}</div>
                  <small>{{ formatDate(doc.updatedAt) }} 수정</small>
                </div>
                <div>
                  <b-button
                    variant="danger"
                    size="sm"
                    @click.stop="confirmDeleteDocument(doc._id, doc.title || doc.filename)"
                  >
                    <i class="ri-delete-bin-line"></i>
                  </b-button>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <!-- 문서 편집 패널 -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header d-flex justify-content-between">
            <div class="header-title">
              <h4 class="card-title">
                {{ selectedDocument ? (selectedDocument.title || selectedDocument.filename) : '문서 내용' }}
              </h4>
            </div>
            <div v-if="selectedDocument">
              <b-button variant="success" size="sm" @click="saveDocument" :disabled="!isDocumentChanged">
                <i class="ri-save-line mr-1"></i> 저장
              </b-button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="!selectedDocument" class="text-center p-5">
              <p>왼쪽 목록에서 문서를 선택하거나 새 문서를 추가하세요.</p>
            </div>
            <div v-else>
              <b-form-textarea
                v-model="editingContent"
                placeholder="문서 내용을 입력하세요..."
                rows="20"
                max-rows="30"
              ></b-form-textarea>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- RAG 챗봇 테스트 패널 -->
    <div v-if="!isLoading" class="row mt-4">
      <div class="col-lg-12">
        <div class="card">
          <div class="card-header">
            <div class="header-title">
              <h4 class="card-title">RAG 챗봇 테스트</h4>
            </div>
          </div>
          <div class="card-body">
            <div class="chat-test-container">
              <!-- 채팅 메시지 표시 영역 -->
              <div class="chat-messages" ref="chatMessages">
                <div v-for="(message, index) in chatMessages" :key="index" class="message-container">
                  <div :class="['message', message.role === 'user' ? 'user-message' : 'bot-message']">
                    <div class="message-content">{{ message.content }}</div>
                    <div v-if="message.sources && message.sources.length > 0" class="message-sources">
                      <div class="sources-title">참고 문서:</div>
                      <ul class="sources-list">
                        <li v-for="(source, idx) in message.sources" :key="idx">
                          {{ source.source }} (관련도: {{ (source.relevance * 100).toFixed(0) }}%)
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 채팅 입력 영역 -->
              <div class="chat-input">
                <b-form-input
                  v-model="chatQuery"
                  placeholder="질문을 입력하세요..."
                  @keyup.enter="sendChatQuery"
                ></b-form-input>
                <b-button variant="primary" @click="sendChatQuery" :disabled="isChatLoading || !chatQuery.trim()">
                  <b-spinner v-if="isChatLoading" small></b-spinner>
                  <i v-else class="ri-send-plane-fill"></i>
                </b-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 새 문서 생성 모달 -->
    <b-modal
      id="new-document-modal"
      ref="newDocumentModal"
      title="새 문서 생성"
      ok-title="생성"
      cancel-title="취소"
      @ok="createNewDocument"
      @hidden="resetNewDocumentForm"
    >
      <b-form-group
        label="파일명:"
        label-for="new-filename"
        :state="newFilenameState"
        :invalid-feedback="newFilenameInvalidFeedback"
      >
        <b-form-input
          id="new-filename"
          v-model="newFilename"
          :state="newFilenameState"
          placeholder="파일명을 입력하세요 (예: guide.txt, info.md)"
        ></b-form-input>
      </b-form-group>

      <b-form-group label="내용:" label-for="new-content">
        <b-form-textarea
          id="new-content"
          v-model="newContent"
          placeholder="문서 내용을 입력하세요..."
          rows="10"
        ></b-form-textarea>
      </b-form-group>
    </b-modal>

    <!-- 문서 삭제 확인 모달 -->
    <b-modal
      id="delete-document-modal"
      ref="deleteDocumentModal"
      title="문서 삭제 확인"
      ok-title="삭제"
      ok-variant="danger"
      cancel-title="취소"
      @ok="deleteDocument"
    >
      <p>정말로 <strong>{{ documentToDeleteTitle }}</strong> 문서를 삭제하시겠습니까?</p>
      <p class="text-danger">이 작업은 되돌릴 수 없습니다.</p>
    </b-modal>
  </div>
</template>

<script>
import kbService from '@/services/kbService';
import confirmInPageMixin from '@/mixins/confirmInPageMixin';

export default {
  mixins: [confirmInPageMixin],
  name: 'KnowledgeBaseManager',
  data() {
    return {
      // 문서 관리
      documents: [],
      selectedDocument: null,
      editingContent: '',
      originalContent: '',

      // 새 문서 생성
      newFilename: '',
      newContent: '',

      // 문서 삭제
      documentToDelete: '',
      documentToDeleteTitle: '',

      // 상태 관리
      isLoading: false,
      loadingMessage: '',
      errorMessage: '',
      successMessage: '',

      // 챗봇 테스트
      chatMessages: [],
      chatQuery: '',
      isChatLoading: false,
      conversationId: null
    };
  },
  computed: {
    // 문서 내용이 변경되었는지 확인
    isDocumentChanged() {
      return this.selectedDocument && this.editingContent !== this.originalContent;
    },

    // 새 파일명 유효성 검사
    newFilenameState() {
      if (!this.newFilename) return null;

      // 파일명 형식 검사 (영문, 숫자, 하이픈, 언더스코어, 점만 허용)
      const validFilenameRegex = /^[a-zA-Z0-9_\-.]+$/;
      return validFilenameRegex.test(this.newFilename);
    },

    // 새 파일명 오류 메시지
    newFilenameInvalidFeedback() {
      if (!this.newFilename) return '파일명을 입력하세요.';
      return '파일명은 영문, 숫자, 하이픈(-), 언더스코어(_), 점(.)만 포함할 수 있습니다.';
    }
  },
  async created() {
    await this.loadDocuments();
  },
  methods: {
    /**
     * 문서 목록 로드
     */
    async loadDocuments() {
      this.isLoading = true;
      this.loadingMessage = '문서 목록을 불러오는 중...';

      try {
        this.documents = await kbService.getDocuments();
        this.documents.sort((a, b) => new Date(b.modified) - new Date(a.modified));
      } catch (error) {
        this.errorMessage = `문서 목록을 불러오는데 실패했습니다: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 문서 선택 및 내용 로드
     */
    async selectDocument(id) {
      if (this.isDocumentChanged) {
        const confirmed = await this.confirmInPage('변경 사항이 저장되지 않았습니다. 계속하시겠습니까?');
        if (!confirmed) return;
      }

      this.isLoading = true;
      this.loadingMessage = '문서 내용을 불러오는 중...';

      try {
        const document = await kbService.getDocument(id);
        this.selectedDocument = document;
        this.editingContent = document.content;
        this.originalContent = document.content;
      } catch (error) {
        this.errorMessage = `문서 내용을 불러오는데 실패했습니다: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 새 문서 모달 표시
     */
    showNewDocumentModal() {
      this.$refs.newDocumentModal.show();
    },

    /**
     * 새 문서 생성
     */
    async createNewDocument(bvModalEvent) {
      // 유효성 검사
      if (!this.newFilename || !this.newFilenameState) {
        bvModalEvent.preventDefault();
        return;
      }

      this.isLoading = true;
      this.loadingMessage = '새 문서를 생성하는 중...';

      try {
        const result = await kbService.createDocument(this.newFilename, this.newContent, this.newFilename);
        this.successMessage = `${this.newFilename} 문서가 생성되었습니다.`;
        await this.loadDocuments();

        // 새로 생성된 문서 선택
        const newDoc = this.documents.find(doc => doc._id === result.documentId);
        if (newDoc) {
          await this.selectDocument(newDoc._id);
        }
      } catch (error) {
        this.errorMessage = `문서 생성에 실패했습니다: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 새 문서 폼 초기화
     */
    resetNewDocumentForm() {
      this.newFilename = '';
      this.newContent = '';
    },

    /**
     * 문서 저장 (수정)
     */
    async saveDocument() {
      if (!this.selectedDocument) return;

      this.isLoading = true;
      this.loadingMessage = '문서를 저장하는 중...';

      try {
        await kbService.updateDocument(
          this.selectedDocument.id,
          this.selectedDocument.title || this.selectedDocument.filename,
          this.editingContent,
          this.selectedDocument.filename,
          this.selectedDocument.category || 'general',
          this.selectedDocument.tags || []
        );
        this.originalContent = this.editingContent;
        this.successMessage = `${this.selectedDocument.title || this.selectedDocument.filename} 문서가 저장되었습니다.`;
        await this.loadDocuments();
      } catch (error) {
        this.errorMessage = `문서 저장에 실패했습니다: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 문서 삭제 확인 모달 표시
     */
    confirmDeleteDocument(id, title) {
      this.documentToDelete = id;
      this.documentToDeleteTitle = title;
      this.$refs.deleteDocumentModal.show();
    },

    /**
     * 문서 삭제
     */
    async deleteDocument() {
      if (!this.documentToDelete) return;

      this.isLoading = true;
      this.loadingMessage = '문서를 삭제하는 중...';

      try {
        await kbService.deleteDocument(this.documentToDelete);
        this.successMessage = `${this.documentToDeleteTitle} 문서가 삭제되었습니다.`;

        // 현재 선택된 문서가 삭제된 경우 선택 해제
        if (this.selectedDocument && this.selectedDocument.id === this.documentToDelete) {
          this.selectedDocument = null;
          this.editingContent = '';
          this.originalContent = '';
        }

        await this.loadDocuments();
      } catch (error) {
        this.errorMessage = `문서 삭제에 실패했습니다: ${error.message}`;
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 날짜 포맷팅
     */
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    /**
     * 챗봇 질문 전송
     */
    async sendChatQuery() {
      if (!this.chatQuery.trim() || this.isChatLoading) return;

      const query = this.chatQuery.trim();
      console.log('[KB-MANAGER] 챗봇 테스트 시작:', query);

      this.chatMessages.push({ role: 'user', content: query });
      this.chatQuery = '';
      this.isChatLoading = true;
      this.errorMessage = '';

      // 스크롤 아래로 이동
      this.$nextTick(() => {
        if (this.$refs.chatMessages) {
          this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
        }
      });

      try {
        // Gemini API 키 가져오기
        console.log('[KB-MANAGER] API 키 확인 중...');
        // 키 값은 안 읽는다 — 서버가 세션 회사 Settings 키를 쓴다
        if (!this.$store.state.settings[0]?.hasGeminiKey) {
          throw new Error('Gemini API 키가 설정되지 않았습니다.');
        }

        console.log('[KB-MANAGER] RAG API 호출 시작...');
        const response = await kbService.chatWithRAG(query, this.conversationId);
        console.log('[KB-MANAGER] RAG API 응답 받음:', response);

        this.chatMessages.push({
          role: 'assistant',
          content: response.response,
          sources: response.sources,
          isRAG: response.isRAG
        });

        console.log('[KB-MANAGER] 메시지 추가 완료, 총 메시지 수:', this.chatMessages.length);

        // 스크롤 아래로 이동
        this.$nextTick(() => {
          if (this.$refs.chatMessages) {
            this.$refs.chatMessages.scrollTop = this.$refs.chatMessages.scrollHeight;
          }
        });
      } catch (error) {
        console.error('[KB-MANAGER] 챗봇 테스트 오류:', error);
        // 서버 글(로그인 필요·Gemini 키 없음 안내)이 있으면 그것을 — axios 의 «status code 400» 대신
        const serverMsg = error.response && error.response.data && (error.response.data.error || error.response.data.message);
        this.errorMessage = serverMsg || `챗봇 응답을 받는데 실패했습니다: ${error.message}`;
      } finally {
        this.isChatLoading = false;
        console.log('[KB-MANAGER] 챗봇 테스트 완료');
      }
    }
  }
};
</script>

<style scoped>
.chat-test-container {
  display: flex;
  flex-direction: column;
  height: 400px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 5px;
  margin-bottom: 15px;
}

.message-container {
  margin-bottom: 15px;
}

.message {
  max-width: 80%;
  padding: 10px 15px;
  border-radius: 10px;
  position: relative;
}

.user-message {
  background-color: #007bff;
  color: white;
  margin-left: auto;
}

.bot-message {
  background-color: #e9ecef;
  color: #212529;
  margin-right: auto;
}

.message-sources {
  margin-top: 8px;
  font-size: 0.8rem;
  color: #6c757d;
}

.sources-title {
  font-weight: bold;
  margin-bottom: 3px;
}

.sources-list {
  margin: 0;
  padding-left: 20px;
}

.chat-input {
  display: flex;
  gap: 10px;
}
</style>
